import { r } from '@/theme/tokens';
import { chartSwatchContainerWidthPx } from './ChartSwatch';
import type { SxProps, Theme } from '@mui/material/styles';

type BuildStackedBarChartTooltipSxParams = {
  borderColor: string;
  textColor: string;
  tooltipFontSize: number | string;
  tooltipFontWeight: number | string;
  tooltipLineHeight: number | string;
};

export const buildStackedBarChartGlobalTooltipStyles = (
  theme: Theme,
): Record<string, Record<string, number | string>> => ({
  '.flowqa-stacked-tooltip-paper': {
    borderRadius: r('md'),
    border: `1px solid ${theme.app.colors.border}`,
    boxShadow: 'none',
    backgroundColor: theme.app.colors.surface,
    color: theme.app.colors.text,
    paddingTop: 10,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: theme.typography.body2.fontSize ?? '13px',
    lineHeight: theme.typography.body2.lineHeight ?? 1.4,
    fontWeight: theme.typography.fontWeightRegular ?? 400,
  },
  '.flowqa-stacked-tooltip-paper *': {
    fontSize: theme.typography.body2.fontSize ?? '13px',
    lineHeight: theme.typography.body2.lineHeight ?? 1.4,
    fontWeight: theme.typography.fontWeightRegular ?? 400,
  },
  '.flowqa-stacked-tooltip-table': {
    borderSpacing: 0,
    border: '0 !important',
    width: 'fit-content !important',
    tableLayout: 'auto',
  },
  '.flowqa-stacked-tooltip-table > thead > tr > th, .flowqa-stacked-tooltip-table > thead > tr > td':
    {
      borderBottom: `1px solid ${theme.app.colors.border} !important`,
      paddingTop: '0px',
      paddingBottom: '8px',
      paddingLeft: '5px !important',
      paddingRight: '5px !important',
      lineHeight: 1.05,
      textAlign: 'left',
      verticalAlign: 'middle !important',
    },
  '.flowqa-stacked-tooltip-table > thead > tr > th': {
    fontWeight: theme.typography.fontWeightRegular ?? 400,
  },
  '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-label': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
  },
  '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-value': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  '.flowqa-stacked-tooltip-table > tbody > tr > th, .flowqa-stacked-tooltip-table > tbody > tr > td':
    {
      border: '0 !important',
      paddingLeft: '6px !important',
      paddingRight: '6px !important',
      verticalAlign: 'middle !important',
    },
  '.flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, .flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
    {
      paddingTop: '8px !important',
      textAlign: 'left !important',
    },
  '.flowqa-stacked-tooltip-table > tbody > tr:first-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:first-of-type > td':
    {
      paddingTop: '6px !important',
    },
  '.flowqa-stacked-tooltip-table > tbody > tr:last-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:last-of-type > td':
    {
      paddingBottom: '6px !important',
    },
  '.flowqa-stacked-tooltip-table .MuiChartsTooltip-markContainer': {
    width: `${chartSwatchContainerWidthPx}px`,
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
  },
  '.flowqa-stacked-tooltip-cell': {
    paddingTop: '3px',
    paddingBottom: '3px',
    lineHeight: 1.1,
  },
  '.flowqa-stacked-tooltip-count-cell': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  '.flowqa-stacked-tooltip-percent-cell': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    minWidth: '40px',
    fontVariantNumeric: 'tabular-nums',
  },
  '.flowqa-stacked-tooltip-mark': {
    marginTop: 0,
    marginBottom: 0,
  },
  '.flowqa-stacked-tooltip-paper.flowqa-stacked-tooltip-paper-empty': {
    paddingBottom: 4,
  },
});

export const buildStackedBarChartTooltipSx = ({
  borderColor,
  textColor,
  tooltipFontSize,
  tooltipFontWeight,
  tooltipLineHeight,
}: BuildStackedBarChartTooltipSxParams): SxProps<Theme> => ({
  fontSize: tooltipFontSize,
  lineHeight: tooltipLineHeight,
  fontWeight: tooltipFontWeight,
  paddingTop: '0 !important',
  paddingBottom: '0 !important',
  paddingLeft: '7px !important',
  paddingRight: '7px !important',
  '& .MuiChartsTooltip-table > thead > tr > th, & .MuiChartsTooltip-table > thead > tr > td, & table > thead > tr > th, & table > thead > tr > td':
    {
      borderBottom: `1px solid ${borderColor} !important`,
      boxShadow: 'none !important',
      paddingLeft: '5px !important',
      paddingRight: '5px !important',
      paddingTop: '0 !important',
      paddingBottom: '6px !important',
      textAlign: 'left',
      verticalAlign: 'middle !important',
    },
  '& .MuiChartsTooltip-table, & table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: 'fit-content !important',
    tableLayout: 'auto',
  },
  '& .MuiChartsTooltip-markContainer': {
    width: `${chartSwatchContainerWidthPx}px !important`,
    display: 'inline-flex !important',
    alignItems: 'center !important',
    verticalAlign: 'middle !important',
  },
  '& .MuiChartsTooltip-row, & tr': {
    lineHeight: '1.1 !important',
  },
  '& .MuiTypography-root, & .MuiChartsTooltip-cell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell, & .MuiChartsTooltip-axisValueCell, & caption, & td, & th':
    {
      color: textColor,
      fontSize: tooltipFontSize,
      lineHeight: tooltipLineHeight,
      fontWeight: tooltipFontWeight,
      textAlign: 'left !important',
    },
  '& .MuiChartsTooltip-cell, & .MuiChartsTooltip-markCell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell':
    {
      paddingTop: '3px !important',
      paddingBottom: '3px !important',
      lineHeight: '1.1 !important',
      verticalAlign: 'middle !important',
    },
  '& .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, & .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
    {
      paddingTop: '8px !important',
      textAlign: 'left !important',
    },
  '& .MuiChartsTooltip-table > tbody > tr:first-of-type > th, & .MuiChartsTooltip-table > tbody > tr:first-of-type > td':
    {
      paddingTop: '6px !important',
    },
  '& .MuiChartsTooltip-table > tbody > tr:last-of-type > th, & .MuiChartsTooltip-table > tbody > tr:last-of-type > td':
    {
      paddingBottom: '2px !important',
    },
  '& .flowqa-stacked-tooltip-count-cell, & .flowqa-stacked-tooltip-percent-cell': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  '& .flowqa-stacked-tooltip-percent-cell': {
    minWidth: '40px',
  },
  '& .MuiChartsTooltip-mark': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
  },
  '& .flowqa-stacked-tooltip-header-total-label': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
  },
  '& .flowqa-stacked-tooltip-header-total-value': {
    textAlign: 'right !important',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  '& .MuiChartsTooltip-axisValue, & .MuiChartsTooltip-axisValueCell': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    lineHeight: '1.05 !important',
  },
});
